import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, HeartHandshake, ShieldCheck, ArrowLeft, Plus, Pencil, Trash2, CheckCircle2, Upload, Star, EyeOff,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  'needs-review': 'bg-blue-50 text-blue-800 border-blue-300',
  approved: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  published: 'bg-emerald-100 text-emerald-900 border-emerald-400',
  hidden: 'bg-red-50 text-red-800 border-red-200',
};

const EMPTY = { name: '', display_name: '', area: '', statement: '', consent: false, status: 'draft', consent_note: '', order: 999, featured: false };

const SupporterDialog = ({ open, supporter, statuses, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(supporter ? { ...EMPTY, ...supporter } : { ...EMPTY }); }, [supporter, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Supporter name is required'); return; }
    setBusy(true);
    try {
      if (supporter?.id) await api.put(`/admin/supporters/${supporter.id}`, form);
      else await api.post('/admin/supporters', form);
      toast.success(supporter?.id ? 'Supporter updated' : 'Supporter added as Draft (private)');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  const uploadPhoto = async (file) => {
    if (!supporter?.id) { toast.error('Save the supporter first, then add a photo'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/admin/supporters/${supporter.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Photo upload failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="supporter-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">{supporter?.id ? 'Edit Supporter' : 'Add Supporter (starts as Draft / private)'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Supporter name *</label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="h-11" data-testid="supporter-name-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Display name (shown publicly if set)</label>
              <Input value={form.display_name} onChange={(e) => set('display_name', e.target.value)} placeholder="e.g. Sandra M." className="h-11" data-testid="supporter-display-name-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Community / area</label>
            <Input value={form.area} onChange={(e) => set('area', e.target.value)} placeholder="e.g. Omemee" className="h-11" data-testid="supporter-area-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Short statement (no contact details, no addresses)</label>
            <Textarea value={form.statement} onChange={(e) => set('statement', e.target.value)} rows={3} data-testid="supporter-statement-input" />
          </div>
          <label className="flex items-start gap-2 text-xs font-bold text-slate-700 cursor-pointer rounded-lg border border-[#C9A84C]/50 bg-[#C9A84C]/5 p-3" data-testid="supporter-consent-label">
            <input type="checkbox" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} className="h-4 w-4 mt-0.5 rounded border-slate-300 accent-[#1a2744]" data-testid="supporter-consent-checkbox" />
            <span>This supporter has given consent to appear publicly<br /><span className="font-normal text-slate-500">Required before Approved/Published. Adults only — no minors. For organizations, attach written permission in the consent note.</span></span>
          </label>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Source / consent note (admin-only)</label>
            <Input value={form.consent_note} onChange={(e) => set('consent_note', e.target.value)} placeholder="e.g. Signed consent form 2026-08-01" className="h-11" data-testid="supporter-consent-note-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Public approval status</label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="h-11" data-testid="supporter-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(statuses).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Order</label>
              <Input type="number" min="0" value={form.order} onChange={(e) => set('order', e.target.value)} className="h-11" data-testid="supporter-order-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
            <Switch checked={form.featured} onCheckedChange={(v) => set('featured', v)} data-testid="supporter-featured-switch" /> Featured
          </label>
          {supporter?.id && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Photo (optional)</label>
              <div className="flex items-center gap-3">
                {supporter.photo && <img src={fileUrl(supporter.photo)} alt="Supporter" className="h-14 w-14 rounded-full object-cover border border-slate-200" data-testid="supporter-photo-preview" />}
                <label className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-300 text-xs font-bold text-slate-600 cursor-pointer hover:bg-slate-50">
                  <Upload className="h-3.5 w-3.5" /> Upload photo
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} data-testid="supporter-photo-input" />
                </label>
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={busy} className="h-11 flex-1 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="supporter-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Save
            </Button>
            <Button onClick={onClose} variant="outline" className="h-11 font-bold">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminSupporters() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [wallBusy, setWallBusy] = useState(false);
  const load = useCallback(() => {
    api.get('/admin/supporters').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveWall = async (patch) => {
    setWallBusy(true);
    try {
      await api.put('/admin/supporters/wall-settings', patch);
      toast.success(patch.display_on !== undefined ? (patch.display_on ? 'Public Supporter Wall is now VISIBLE' : 'Public Supporter Wall hidden') : 'Wall settings saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setWallBusy(false);
  };

  const toggleDisplay = (v) => {
    if (v && !window.confirm('Turn the PUBLIC Supporter Wall ON?\n\nOnly supporters with consent checked AND status Approved/Published will appear. Confirm Steve has approved publishing this page.')) return;
    saveWall({ display_on: v });
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/supporters/${id}`);
      toast.success('Supporter removed');
      load();
    } catch { toast.error('Delete failed'); }
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="supporters-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the Supporter Wall.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-supporters">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><HeartHandshake className="h-6 w-6 text-[#C9A84C]" /> Supporter Wall</h1>
          <p className="text-sm text-slate-500 mt-1">Privacy-safe endorsements preparation — the public page stays hidden until Steve approves publishing.</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="supporters-back-pages-btn">
            <Link to="/admin/pages"><ArrowLeft className="h-4 w-4 mr-1.5" /> Pages</Link>
          </Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="supporters-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Supporter
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-4" data-testid="supporters-safety-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      <div className={`rounded-xl border p-4 mb-5 ${data.wall.display_on ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`} data-testid="supporters-wall-settings">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2">
              {data.wall.display_on ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
              Public page: {data.wall.display_on ? 'VISIBLE at /supporters' : 'Hidden / Not Published (default)'}
            </p>
            <p className="text-xs text-slate-500 mt-1">{data.public_count} supporter{data.public_count === 1 ? '' : 's'} would appear publicly (consent + Approved/Published).</p>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600 shrink-0">
            <Switch checked={data.wall.display_on} onCheckedChange={toggleDisplay} disabled={wallBusy} data-testid="supporters-display-toggle" />
            Public display
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Page title</label>
            <Input defaultValue={data.wall.title} onBlur={(e) => e.target.value !== data.wall.title && saveWall({ title: e.target.value })} className="h-11" data-testid="supporters-wall-title" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Public disclaimer</label>
            <Input defaultValue={data.wall.disclaimer} onBlur={(e) => e.target.value !== data.wall.disclaimer && saveWall({ disclaimer: e.target.value })} className="h-11" data-testid="supporters-wall-disclaimer" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">Subtitle</label>
            <Input defaultValue={data.wall.subtitle} onBlur={(e) => e.target.value !== data.wall.subtitle && saveWall({ subtitle: e.target.value })} className="h-11" data-testid="supporters-wall-subtitle" />
          </div>
        </div>
      </div>

      {data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="supporters-empty">
          <HeartHandshake className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#1a2744]">No supporters recorded yet</p>
          <p className="text-xs text-slate-500 mt-1">Add supporters as Drafts — nothing appears publicly without consent, approval, and the page toggle.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="supporters-list">
          {data.items.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`supporter-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  {s.photo ? <img src={fileUrl(s.photo)} alt="" className="h-10 w-10 rounded-full object-cover border border-slate-200" /> : <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-bold">{(s.display_name || s.name || '?')[0]}</div>}
                  <div>
                    <p className="font-serif font-bold text-[#1a2744] text-sm">{s.name}{s.display_name ? ` (shows as "${s.display_name}")` : ''}</p>
                    <p className="text-xs text-slate-500">{s.area || 'No community set'}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_STYLES[s.status]}`}>{data.statuses[s.status]}</Badge>
                  {s.featured && <Badge variant="outline" className="text-[10px] font-bold bg-[#C9A84C]/10 border-[#C9A84C]/50 text-[#7A5C12]"><Star className="h-2.5 w-2.5 mr-0.5" /> Featured</Badge>}
                </div>
              </div>
              {s.statement && <p className="text-xs text-slate-600 mt-2 italic">“{s.statement}”</p>}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {s.consent
                  ? <Badge variant="outline" className="text-[10px] font-bold bg-emerald-50 border-emerald-300 text-emerald-800" data-testid={`supporter-consent-badge-${s.id}`}><ShieldCheck className="h-2.5 w-2.5 mr-0.5" /> Verified Consent</Badge>
                  : <Badge variant="outline" className="text-[10px] font-bold bg-red-50 border-red-200 text-red-800">No consent recorded</Badge>}
                {s.consent_note && <span className="text-[10px] text-slate-400">{s.consent_note}</span>}
                {s.date_approved && <span className="text-[10px] text-slate-400">Approved {s.date_approved.slice(0, 10)}</span>}
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => { setEditing(s); setDialogOpen(true); }} className="h-8 text-xs font-bold" data-testid={`supporter-edit-${s.id}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s.id)} className="h-8 text-xs font-bold text-red-500" data-testid={`supporter-delete-${s.id}`}>
                  <Trash2 className="h-3 w-3 mr-1" /> Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SupporterDialog open={dialogOpen} supporter={editing} statuses={data.statuses} onClose={() => setDialogOpen(false)} onSaved={load} />
    </div>
  );
}
